import { z } from 'zod';
import { INTERNAL_ONLY, type Visibility } from '../../domain/entities/common.ts';
import {
  DailyLogSchema,
  DEFAULT_LOG_TITLE,
  type DailyLog,
} from '../../domain/entities/daily-log.ts';
import { DateOnlySchema } from '../../domain/date-only.ts';
import { newId } from '../../domain/id.ts';
import { expectUnchanged } from '../../ports/batch.ts';
import type { MemberKind } from '../../ports/auth-port.ts';
import type { ClockPort } from '../../ports/clock-port.ts';
import type { DataPort } from '../../ports/data-port.ts';
import type { Query, Unsubscribe } from '../../ports/query.ts';
import { NotFoundError } from '../../ports/repository.ts';

/**
 * Writing a daily log.
 *
 * The record of what happened on a job on a day: what was done, who was
 * there, what stopped. Several per day are allowed — two crews, two logs —
 * so nothing here is keyed by date, and the list groups instead.
 */
export class DailyLogError extends Error {
  readonly reason: 'invalid' | 'notYours';

  constructor(reason: 'invalid' | 'notYours', message: string) {
    super(message);
    this.name = 'DailyLogError';
    this.reason = reason;
  }
}

export const SaveDailyLogInputSchema = z.object({
  date: DateOnlySchema,
  title: z.string().trim().min(1, 'A log needs a title').max(200).optional(),
  notes: z.string().max(20_000).optional(),
  tagIds: z.array(z.string().min(1)).optional(),
  customFields: z.record(z.string().min(1), z.array(z.string())).optional(),
  visibility: z.custom<Visibility>().optional(),
  notifyUids: z.array(z.string().min(1)).optional(),
  weatherNotes: z.string().max(2_000).optional(),
});
export type SaveDailyLogInput = z.input<typeof SaveDailyLogInputSchema>;

export interface DailyLogContext {
  readonly data: DataPort;
  readonly clock: ClockPort;
  readonly companyId: string;
  readonly jobId: string;
  readonly uid: string;
}

function repo(context: Pick<DailyLogContext, 'data' | 'companyId' | 'jobId'>) {
  return context.data.collection<DailyLog>(
    { companyId: context.companyId, jobId: context.jobId },
    'dailyLogs',
  );
}

export async function createDailyLog(
  context: DailyLogContext,
  input: SaveDailyLogInput,
): Promise<DailyLog> {
  const parsed = SaveDailyLogInputSchema.safeParse(input);
  if (!parsed.success) throw new DailyLogError('invalid', parsed.error.issues[0]!.message);

  const now = context.clock.now().toISOString();
  const log: DailyLog = {
    id: newId(),
    companyId: context.companyId,
    jobId: context.jobId,
    date: parsed.data.date,
    title: parsed.data.title ?? DEFAULT_LOG_TITLE,
    notes: parsed.data.notes ?? '',
    tagIds: parsed.data.tagIds ?? [],
    customFields: parsed.data.customFields ?? {},
    visibility: parsed.data.visibility ?? INTERNAL_ONLY,
    notifyUids: parsed.data.notifyUids ?? [],
    weatherNotes: parsed.data.weatherNotes,
    createdAt: now,
    createdBy: context.uid,
    updatedAt: now,
    updatedBy: context.uid,
  };

  const checked = DailyLogSchema.safeParse(log);
  if (!checked.success) throw new DailyLogError('invalid', checked.error.issues[0]!.message);

  await repo(context).create(log);
  return log;
}

/**
 * Change a log.
 *
 * Guarded with the version it read, so two people editing the same log do
 * not silently overwrite each other's notes — the field most likely to be
 * typed into by two people at once on a job that had a bad day.
 *
 * Marking somebody else's log private is refused, for the same reason as
 * for a file: `private` restricts a record to its creator, so doing it to a
 * colleague's log would hide it from you the instant it landed.
 */
export async function updateDailyLog(
  context: DailyLogContext,
  id: string,
  input: SaveDailyLogInput,
): Promise<DailyLog> {
  const parsed = SaveDailyLogInputSchema.safeParse(input);
  if (!parsed.success) throw new DailyLogError('invalid', parsed.error.issues[0]!.message);

  const existing = await repo(context).get(id);
  if (!existing) throw new NotFoundError('dailyLogs', id);

  if (parsed.data.visibility?.private && existing.createdBy !== context.uid) {
    throw new DailyLogError(
      'notYours',
      'That log was written by somebody else, so it cannot be made private',
    );
  }

  const next: DailyLog = {
    ...existing,
    date: parsed.data.date,
    title: parsed.data.title ?? existing.title,
    notes: parsed.data.notes ?? existing.notes,
    tagIds: parsed.data.tagIds ?? existing.tagIds,
    customFields: parsed.data.customFields ?? existing.customFields,
    visibility: parsed.data.visibility ?? existing.visibility,
    notifyUids: parsed.data.notifyUids ?? existing.notifyUids,
    weatherNotes: parsed.data.weatherNotes ?? existing.weatherNotes,
    updatedAt: context.clock.now().toISOString(),
    updatedBy: context.uid,
  };

  const checked = DailyLogSchema.safeParse(next);
  if (!checked.success) throw new DailyLogError('invalid', checked.error.issues[0]!.message);

  const { id: _id, ...patch } = next;
  await context.data.commit([
    {
      kind: 'update',
      scope: { companyId: context.companyId, jobId: context.jobId },
      collection: 'dailyLogs',
      id,
      patch,
      expect: expectUnchanged(existing),
    },
  ]);
  return next;
}

/**
 * Delete a log.
 *
 * Does not touch its attachments. The photographs taken on a day outlive
 * the log that mentioned them, and `docs/features/11-files-messaging.md` §1
 * is explicit about it — they stay in the job's Photos folder with their
 * link to a record that no longer exists, which `linkedRecords` tolerates.
 */
export async function deleteDailyLog(context: DailyLogContext, id: string): Promise<void> {
  const existing = await repo(context).get(id);
  if (!existing) throw new NotFoundError('dailyLogs', id);
  await repo(context).delete(id);
}

// ---- reading -------------------------------------------------------------

/**
 * The queries whose union is "the logs on this job you may see".
 *
 * Shaped by the caller's member kind, because the `list` rule is: an
 * internal member asks for everything not private plus their own private
 * ones, a sub asks for `visibility.subs == true`, an owner for
 * `visibility.owner == true`. Security rules are not filters, so a query
 * that could match something the caller may not see is refused outright —
 * and a query that asks for more than the rule allows fails rather than
 * being trimmed.
 *
 * The kind is a parameter rather than two functions because the portal and
 * the internal app would otherwise each have their own idea of what the
 * rule says, and one of them would eventually be wrong.
 */
export function visibleLogQueries(uid: string, kind: MemberKind = 'internal'): readonly Query[] {
  const newestFirst = [{ field: 'date', direction: 'desc' as const }];

  if (kind === 'sub' || kind === 'client') {
    const audience = kind === 'sub' ? 'visibility.subs' : 'visibility.owner';
    // One query, not two: an external member has no private logs of their
    // own — they cannot write one at all.
    return [
      {
        where: [
          { field: 'visibility.private', op: '==', value: false },
          { field: audience, op: '==', value: true },
        ],
        orderBy: newestFirst,
      },
    ];
  }

  return [
    {
      where: [{ field: 'visibility.private', op: '==', value: false }],
      orderBy: newestFirst,
    },
    {
      where: [
        { field: 'visibility.private', op: '==', value: true },
        { field: 'createdBy', op: '==', value: uid },
      ],
      orderBy: newestFirst,
    },
  ];
}

/** One list from several, newest day first, with no log counted twice. */
export function mergeLogs(...pages: readonly (readonly DailyLog[])[]): DailyLog[] {
  const byId = new Map<string, DailyLog>();
  for (const page of pages) {
    for (const log of page) byId.set(log.id, log);
  }
  return [...byId.values()].sort(
    (a, b) =>
      b.date.localeCompare(a.date) ||
      b.createdAt.localeCompare(a.createdAt) ||
      a.id.localeCompare(b.id),
  );
}

export async function listDailyLogs(
  context: Pick<DailyLogContext, 'data' | 'companyId' | 'jobId' | 'uid'>,
  kind: MemberKind = 'internal',
): Promise<DailyLog[]> {
  const pages = await Promise.all(
    visibleLogQueries(context.uid, kind).map((query) => repo(context).list(query)),
  );
  return mergeLogs(...pages.map((page) => page.items));
}

/** The same, live. Nothing is reported until both queries have answered. */
export function subscribeToDailyLogs(
  context: Pick<DailyLogContext, 'data' | 'companyId' | 'jobId' | 'uid'>,
  onChange: (logs: readonly DailyLog[]) => void,
  onError: (cause: Error) => void,
  kind: MemberKind = 'internal',
): Unsubscribe {
  const queries = visibleLogQueries(context.uid, kind);
  const pages: (readonly DailyLog[] | undefined)[] = queries.map(() => undefined);

  const emit = () => {
    const answered: (readonly DailyLog[])[] = [];
    for (const page of pages) {
      if (page === undefined) return;
      answered.push(page);
    }
    onChange(mergeLogs(...answered));
  };

  const stops = queries.map((query, index) =>
    repo(context).subscribe(
      query,
      (page) => {
        pages[index] = page.items;
        emit();
      },
      onError,
    ),
  );

  return () => {
    for (const stop of stops) stop();
  };
}

// ---- shaping for the list page ------------------------------------------

export interface DailyLogFilter {
  readonly from?: string;
  readonly to?: string;
  readonly tagIds?: readonly string[];
  readonly createdBy?: string;
  readonly query?: string;
  /** Who was on site, matched against any custom field's values. */
  readonly personId?: string;
}

export function filterDailyLogs(logs: readonly DailyLog[], filter: DailyLogFilter): DailyLog[] {
  const needle = filter.query?.trim().toLowerCase();
  return logs.filter((log) => {
    if (filter.from && log.date < filter.from) return false;
    if (filter.to && log.date > filter.to) return false;
    if (filter.createdBy && log.createdBy !== filter.createdBy) return false;
    if (filter.tagIds && filter.tagIds.length > 0) {
      if (!filter.tagIds.some((tagId) => log.tagIds.includes(tagId))) return false;
    }
    if (filter.personId) {
      const named = Object.values(log.customFields).some((values) =>
        values.includes(filter.personId!),
      );
      if (!named) return false;
    }
    if (needle && !`${log.title} ${log.notes}`.toLowerCase().includes(needle)) return false;
    return true;
  });
}

export interface LogsOnDay {
  readonly date: string;
  readonly logs: readonly DailyLog[];
}

/**
 * Grouped by day, newest day first.
 *
 * The list page is a date-ordered feed and several logs can share a day, so
 * the heading is the day and the logs sit under it. Grouping here rather
 * than in the component keeps it testable without a browser.
 */
export function groupLogsByDate(logs: readonly DailyLog[]): LogsOnDay[] {
  const byDate = new Map<string, DailyLog[]>();
  for (const log of logs) {
    const existing = byDate.get(log.date);
    if (existing) existing.push(log);
    else byDate.set(log.date, [log]);
  }
  return [...byDate.entries()]
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([date, group]) => ({
      date,
      logs: [...group].sort(
        (a, b) => b.createdAt.localeCompare(a.createdAt) || a.id.localeCompare(b.id),
      ),
    }));
}
